/**
 * Implements software requirements: SRS-215
 *
 * The server refuses (412) a mutation that names an older case version, so a session cannot
 * overwrite changes that it has not seen.
 */

/** Same text as the server's 412 message. */
export const STALE_CASE_MESSAGE = 'The case was changed by another session. Reload the case.';

const MUTATING_METHODS = new Set(['put', 'post', 'patch', 'delete']);

const CASE_ROUTE = /^(?:project|cases)\/(\d+)(?:[/?#]|$)/;

const ORIGIN = /^https?:\/\/[^/]+/i;

/** Uploads the server does not guard; they must stay free to run in parallel. */
const EXEMPT_ROUTES = [
    /^cases\/\d+\/studies\/[^/]+\/files(?:\/|$)/,
    /^project\/\d+\/study\/[^/]+\/xray\/[^/]+/,
    /^cases\/\d+\/hip-spine-studies\/[^/]+\/files\/[^/]+/,
    /^project\/\d+\/study\/[^/]+\/file(?:\/zipped)?\/?(?:[?#]|$)/,
];

const DIGITS = /^\d+$/;

function _apiPath(url: string, baseUrl: string): string {
    const relative =
        baseUrl && url.startsWith(baseUrl) ? url.slice(baseUrl.length) : url.replace(ORIGIN, '');
    return relative.replace(/^\/+/, '');
}

export function isCaseVersionExempt(apiPath: string): boolean {
    return EXEMPT_ROUTES.some((route) => route.test(apiPath));
}

/** Null for a read, a case creation (no id yet), an exempt upload or a route outside a case. */
export function caseIdFromMutationUrl(url: string, method: string, baseUrl = ''): number | null {
    if (!MUTATING_METHODS.has(method.toLowerCase())) return null;

    const path = _apiPath(url, baseUrl);
    if (isCaseVersionExempt(path)) return null;

    const match = CASE_ROUTE.exec(path);
    return match ? Number(match[1]) : null;
}

function _isVersion(version: number | undefined): version is number {
    return version !== undefined && Number.isSafeInteger(version) && version >= 0;
}

export function parseCaseVersion(raw: string): number | undefined {
    const text = raw.trim();
    if (!DIGITS.test(text)) return undefined;
    const version = Number(text);
    return _isVersion(version) ? version : undefined;
}

const _versions = new Map<number, number>();

/** Cases refused as stale: their version is frozen until the page reloads. */
const _staleCases = new Set<number>();

/** Only a higher version is kept, so a slow response cannot move the version back. */
export function observeCaseVersion(caseId: number, version: number | undefined): void {
    if (!_isVersion(version) || _staleCases.has(caseId)) return;
    const known = _versions.get(caseId);
    if (known === undefined || version > known) _versions.set(caseId, version);
}

/** Cases the session left: the next read of each replaces its version. */
const _releasedCases = new Set<number>();

/**
 * Seeds only a first or released version: a later read would adopt another session's unloaded
 * changes, and the next save would overwrite them.
 */
export function seedCaseVersion(caseId: number, version: number | undefined): void {
    if (_releasedCases.has(caseId) && _isVersion(version) && !_staleCases.has(caseId)) {
        _releasedCases.delete(caseId);
        _versions.set(caseId, version);
    } else if (!_versions.has(caseId)) {
        observeCaseVersion(caseId, version);
    }
}

/**
 * Released, not deleted: a pending save must still name its old version, so the server refuses
 * it if another session changed the case.
 */
export function releaseCaseVersion(caseId: number): void {
    _releasedCases.add(caseId);
}

export function currentCaseVersion(caseId: number): number | undefined {
    return _versions.get(caseId);
}

/** For tests only. */
export function resetCaseVersions(): void {
    _versions.clear();
    _staleCases.clear();
    _releasedCases.clear();
}

export class StaleCaseError extends Error {
    readonly caseId: number;
    readonly currentVersion: number | undefined;

    constructor(caseId: number, currentVersion: number | undefined) {
        super(STALE_CASE_MESSAGE);
        this.name = 'StaleCaseError';
        this.caseId = caseId;
        this.currentVersion = currentVersion;
        Object.setPrototypeOf(this, StaleCaseError.prototype);
    }
}

export type CaseStaleListener = (error: StaleCaseError) => void;

const _listeners = new Set<CaseStaleListener>();

export function onCaseStale(listener: CaseStaleListener): () => void {
    _listeners.add(listener);
    return () => {
        _listeners.delete(listener);
    };
}

/** Also freezes the case version until the page reloads. */
export function notifyCaseStale(error: StaleCaseError): void {
    _staleCases.add(error.caseId);
    [..._listeners].forEach((listener) => listener(error));
}
